import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Minus, Plus } from 'lucide-react';
import { FadeInSection } from './FadeInSection';
import { useLanguage } from './LanguageContext';
type FAQItem = {
  question: string;
  answer: React.ReactNode;
};
const faqContent: Record<'en' | 'es', {
  eyebrow: string;
  title: string;
  items: FAQItem[];
}> = {
  en: {
    eyebrow: 'Frequently Asked Questions',
    title: 'What My Clients Ask',
    items: [{
      question: 'What do I need to buy a home in Minnesota?',
      answer: <>
            <p data-id="element-68" data-mp="203jbjphbvfej">
              To purchase a property in Minnesota you will need the following
              documents:
            </p>
            <div className="mt-5 space-y-4" data-id="element-69" data-mp="1v4g6i51cl96k">
              <p data-id="element-70" data-mp="oovyv0d9myue">
                <strong data-id="element-71" data-mp="opfbq2o26x1k">If you have a Social Security number:</strong> ID and
                Social Security card, 2024–2025 tax returns and W2s, last two
                pay stubs, and your most recent bank statement.
              </p>
              <p data-id="element-72" data-mp="1ko9oy1h9ai0m">
                <strong data-id="element-73" data-mp="1klnglzmjjyhc">If you have an ITIN number:</strong> ID, Green ITIN
                sheet, 2024–2025 tax returns W2 or 1099, last two pay stubs, and
                your most recent bank statement.
              </p>
              <p data-id="element-74" data-mp="1fonf820mzjwk">
                <strong data-id="element-75" data-mp="1fo42czpuflpe">If you are a business owner:</strong> ID and Social
                Security card, 2024–2025 tax returns and 1099 forms, last two
                pay stubs, and your most recent bank statement.
              </p>
            </div>
          </>
    }, {
      question: 'Do I need a minimum income to buy a property?',
      answer: 'Yes, you must earn at least $60,000 per year. One option is to add a family member or friend to the purchase who meets the same loan requirements.'
    }, {
      question: 'Are there assistance programs in Minnesota to help me buy a home?',
      answer: 'Yes, there are many programs available, designed for lower-income families. It is very important to know how much you earn and how many people are in your household, for example whether you are married, single, and the number of children.'
    }, {
      question: 'How can I find out if I qualify to buy a home?',
      answer: 'The best way to find out if you qualify is to schedule an appointment and bring your income information. At that meeting, a credit advisor and I will be there to answer your specific questions. This makes the visit very productive and turns your goal of buying a home into something practical and achievable.'
    }, {
      question: 'What type of property can you help me purchase?',
      answer: 'I can help you buy a condo, townhome, twinhome, or a house. If you want something for multiple families, I can help you purchase a duplex, triplex, or fourplex. With this option, in addition to having a place to live, you can rent to other families and use that rental income to help pay your mortgage.'
    }]
  },
  es: {
    eyebrow: 'Preguntas Frecuentes',
    title: 'Lo Que Mis Clientes Preguntan',
    items: [{
      question: '¿Qué necesito para comprar una casa en Minnesota?',
      answer: <>
            <p data-id="element-76" data-mp="cggrp70w8s5d">
              Para comprar una propiedad en Minnesota debes tener los siguientes
              documentos:
            </p>
            <div className="mt-5 space-y-4" data-id="element-77" data-mp="hei8flaukfdo">
              <p data-id="element-78" data-mp="me3ba6t517vo">
                <strong data-id="element-79" data-mp="mdlcb2f3cfk2">Si tienes número de seguro social:</strong>{' '}
                Identificación y tarjeta del seguro social, taxes del 2024–2025
                y las W2, dos últimos talones de cheque, y un último estado de
                cuenta del banco.
              </p>
              <p data-id="element-80" data-mp="2s7bavhij6uwj">
                <strong data-id="element-81" data-mp="2s7uvj5zkfxe9">Si tienes ITIN number:</strong> Identificación, la hoja
                verde del ITIN, taxes del 2024–2025 W2 o 1099, dos últimos
                talones de cheque, y un último estado de cuenta del banco.
              </p>
              <p data-id="element-82" data-mp="21u50rsx82bnv">
                <strong data-id="element-83" data-mp="21xspmerq29w9">Si eres dueño de un negocio:</strong> Identificación y
                tarjeta del seguro social, taxes del 2024–2025 y las formas
                1099, dos últimos talones de cheque, y un último estado de
                cuenta del banco.
              </p>
            </div>
          </>
    }, {
      question: '¿Debo contar con un ingreso mínimo para comprar una propiedad?',
      answer: 'Sí, debes ganar mínimo $60,000 dólares al año. Puedes agregar a un familiar o un amigo para comprar, que cumpla con los mismos requisitos de préstamo.'
    }, {
      question: '¿Existen ayudas que puedo obtener del estado de Minnesota para la compra de mi casa?',
      answer: 'Sí, son muchas y variadas. Están diseñadas para las familias de bajos recursos. Es muy importante saber cuánto dinero ganas y cuántas personas conforman tu núcleo familiar, por ejemplo si eres casado, soltero y el número de hijos.'
    }, {
      question: '¿Cómo puedo saber si califico para comprar una casa?',
      answer: 'La mejor manera es asistir a una cita con tu propia información de ingresos. En esa reunión estaremos un asesor de crédito y yo para hablar de tus preguntas concretas. Esto hace que la visita sea muy exitosa y que la meta de comprar casa se logre de manera práctica y exitosa.'
    }, {
      question: '¿Qué tipo de propiedad me puedes ayudar a adquirir?',
      answer: 'Te puedo ayudar a comprar un condominio, townhome, twinhome o una casa. Si quieres algo para más familias, te ayudo a comprar un dúplex, triplex o fourplex. Con esta opción puedes rentar a otras familias y usar ese ingreso para pagar tu préstamo.'
    }]
  }
};
export function FAQ() {
  const {
    lang
  } = useLanguage();
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const content = faqContent[lang];
  function toggleItem(index: number) {
    setOpenIndex(current => current === index ? null : index);
  }
  return <FadeInSection id="faq" className="w-full bg-[#faf8f5] py-[100px]" data-id="element-84" data-mp="ay70xubxv6ve">
      <div className="mx-auto max-w-5xl px-6 lg:px-12" data-id="element-85" data-mp="666stelpt7nq">
        <div className="mb-16 flex flex-col items-center text-center" data-id="element-86" data-mp="64m07uiynysi">
          <span className="eyebrow mb-4" data-id="element-87" data-mp="17cb6fzhhp57">{content.eyebrow}</span>
          <h2 className="font-serif-display text-3xl uppercase tracking-wider text-[#2c2c2c] md:text-4xl" data-id="element-88" data-mp="3tby3qr4kk52f">
            {content.title}
          </h2>
          <div className="mt-8 h-px w-[60px] bg-[#c9a96e]" data-id="element-89" data-mp="3oeo7osmocv5m" />
        </div>

        <div className="border-t border-[#c9a96e]/70" data-id="element-90" data-mp="3je0h282cygbx">
          {content.items.map((item, index) => {
          const isOpen = openIndex === index;
          const panelId = `faq-panel-${lang}-${index}`;
          return <article key={item.question} className="border-b border-[#c9a96e]/70" data-id="element-91" data-mp="1hjrdttkmrhqw" data-mpi={index}>
                <button type="button" onClick={() => toggleItem(index)} aria-expanded={isOpen} aria-controls={panelId} className="flex w-full items-center justify-between gap-6 py-7 text-left transition-colors duration-300 hover:text-[#c9a96e] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#c9a96e] focus-visible:ring-offset-4" data-id="element-92" data-mp="1l4kr8tf2uxqe">
                  <span className="font-serif-display text-lg uppercase tracking-[0.08em] text-[#2c2c2c] md:text-xl" data-id="element-93" data-mp="1279vvjwkgog8">
                    {item.question}
                  </span>
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center text-[#c9a96e]" aria-hidden="true" data-id="element-94" data-mp="1b4afld8oxydc">
                    {isOpen ? <Minus size={24} strokeWidth={1.25} data-id="element-95" data-mp="2mksfxofesl59" /> : <Plus size={24} strokeWidth={1.25} data-id="element-96" data-mp="2rik36553zj34" />}
                  </span>
                </button>

                <AnimatePresence initial={false} data-id="element-97" data-mp="3cltas93mdb73">
                  {isOpen && <motion.div id={panelId} role="region" aria-label={item.question} initial={{
                height: 0,
                opacity: 0
              }} animate={{
                height: 'auto',
                opacity: 1
              }} exit={{
                height: 0,
                opacity: 0
              }} transition={{
                duration: 0.3,
                ease: 'easeOut'
              }} className="overflow-hidden" data-id="element-98" data-mp="38of1khfxv3f3">
                      <div className="body-text max-w-4xl pb-8 pr-12 text-[15px] leading-8 text-[#666666] md:text-base" data-id="element-99" data-mp="260ip20rpldiv">
                        {item.answer}
                      </div>
                    </motion.div>}
                </AnimatePresence>
              </article>;
        })}
        </div>
      </div>
    </FadeInSection>;
}